import { gql } from 'graphql-request';

import { audiobookQueries } from 'shared/graphql/queries/audiobooks';
import { blogQueries } from 'shared/graphql/queries/blog';
import { collectionQueries } from 'shared/graphql/queries/collections';
import { bookClubQueries } from 'shared/graphql/queries/book_clubs';
import { promotionQueries } from 'shared/graphql/queries/promotions';
import { shoppingCartQueries } from 'shared/graphql/queries/shopping_cart';
import { wishlistQueries } from 'shared/graphql/queries/wishlist';

const audiobookPromotedCredits = gql`
  query FetchPromotedAuthors($audiobookId: ID!) {
    audiobook(id: $audiobookId) {
      promotedAuthors {
        id
        name
        slug
        followedByCurrentUser
      }
      promotedNarrators {
        id
        name
        slug
        followedByCurrentUser
      }
    }
  }
`;

const currentAddress = gql`
  query fetchCurrentUserBillingAddress {
    currentUserBillingAddress {
      id
      state
      zip
    }
    currentUser {
      countryCode
      firstName
      lastName
    }
  }
`;

const currentGiftCardBalance = gql`
  query fetchCurrentUserGiftCardBalance($prettyAppliedAmount: String) {
    currentUser {
      giftCardBalance(prettyAppliedAmount: $prettyAppliedAmount)
    }
  }
`;

const currentProfile = gql`
  query fetchCurrentUserProfile {
    currentUserBillingAddress {
      id
      state
      zip
    }
    currentUser {
      id
      firstName
      lastName
      email
      countryCode
    }
  }
`;

const currentUserAudiobooks = gql`
  query FetchCurrentUserAudiobooks(
    $filter: UserAudiobooksFilter
    $sort: UserAudiobooksSort
    $page: Int
    $pageSize: Int
    $hideArchived: Boolean
    $searchTerm: String
    $filters: [UserAudiobooksFilter!]
    $omitUserCollectionIds: [ID!]
  ) {
    currentUserAudiobooks(
      filter: $filter
      sort: $sort
      page: $page
      pageSize: $pageSize
      hideArchived: $hideArchived
      searchTerm: $searchTerm
      filters: $filters
      omitUserCollectionIds: $omitUserCollectionIds
    ) {
      id
      progressStatus
      positionPercent
      playable

      audiobook {
        id
        url
        coverUrl
        displayTitle
        displayAuthors
        displayNarrators
        currentProduct {
          listingPrice
          discountPrice
          savingsPercent
          timeLeft
        }
        supplements {
          url
        }
      }

      userReview {
        id
        hasReview
        rating
      }
    }

    currentUserAudiobooksCount(
      filter: $filter
      hideArchived: $hideArchived
      searchTerm: $searchTerm
      filters: $filters
      omitUserCollectionIds: $omitUserCollectionIds
    )
  }
`;

const currentUserCollections = gql`
  query FetchCurrentUserCollections {
    currentUserCollections {
      id
      title
      audiobooks {
        coverUrl
        displayAuthors
        displayTitle
        id
      }
    }
  }
`;

const currentUserCollectionsBasic = gql`
  query FetchCurrentUserCollections {
    currentUserCollections {
      id
      title
    }
  }
`;

const currentUserFollowedAuthors = gql`
  query FetchCurrentUserFollowedAuthors($page: Int!, $affinityType: UserPersonFollowAffinity) {
    currentUserFollowedAuthors(affinityType: $affinityType, sort: DEFAULT) {
      totalCount
      objects(page: $page) {
        ... on Author {
          id
          name
          slug
          url
          typeDescription
        }
      }
    }
  }
`;

const recommendedWeakFollowAuthors = gql`
  query FetchRecommendedWeakFollowAuthors($page: Int!, $affinityType: UserPersonFollowAffinity) {
    currentUserFollowedAuthors(affinityType: $affinityType, sort: USER_FOLLOW_SOURCE) {
      totalCount
      objects(page: $page, pageSize: 10) {
        ... on Author {
          id
          name
          url
          slug
          typeDescription
          summaryAudiobooks(limit: 4) {
            id
            coverUrl
          }
        }
      }
    }
  }
`;

const currentUserSuggestedAuthors = gql`
  query FetchCurrentUserSuggestedAuthors($page: Int!, $seed: Int) {
    currentUserSuggestedAuthors(sort: RANDOM, seed: $seed) {
      totalCount
      objects(page: $page, pageSize: 10) {
        ... on Author {
          id
          name
          url
          slug
          typeDescription
          summaryAudiobooks(limit: 4) {
            id
            coverUrl
          }
        }
      }
    }
  }
`;

const promotionalBundles = gql`
  query FetchPromotionalBundles($page: Int, $pageSize: Int, $tags: [String!], $previewDate: String) {
    promotionalBundles(tags: $tags, previewDate: $previewDate) {
      totalCount
      objects(page: $page, pageSize: $pageSize) {
        ... on PromotionalBundle {
          id
          displayName
          tagline
          description
          clickthroughPath
          bundlePriceText
          savingsText
          audiobooks {
            id
            averageRating
            allAuthors {
              name
              slug
              url
            }
            bookDescription
            bookDescriptionShort
            coverUrl
            currentProduct {
              discountPrice
              isFreeListing
              showListingPrice
              listingPrice
              purchaseUrl
            }
            displayAuthors
            displayTitle
            editorialDescription
            ratingsCount
            runtime
            url
          }
        }
      }
    }
  }
`;

const promotionalBundle = gql`
  query FetchPromotionalBundle($id: ID!) {
    promotionalBundle(id: $id) {
      id
      displayName
      audiobooks {
        id
        allAuthors {
          name
          slug
          url
        }
        allNarrators {
          name
          slug
          url
        }
        averageRating
        bookDescription
        bookDescriptionShort
        coverUrl
        currentProduct {
          discountPrice
          isFreeListing
          listingPrice
          purchaseUrl
          salableInCurrentCountry
          showListingPrice
        }
        currentUserShoppingCartItem {
          id
        }
        currentUserUserAudiobook {
          id
        }
        currentUserWishlistItem {
          id
        }
        displayAuthors
        displayTitle
        editorialDescription
        ratingsCount
        runtime
        sampleUrl
        seriesName
        seriesNumber
        url
      }
    }
  }
`;

const tagSuggestedPeople = gql`
  query FetchTagSuggestedPeople($tag: String!, $page: Int!, $seed: Int) {
    tagSuggestedPeople(tag: $tag, sort: RANDOM, seed: $seed) {
      totalCount
      objects(page: $page, pageSize: 12) {
        ... on Author {
          id
          name
          url
          slug
          typeDescription
          paginatedAudiobooks(filterCurrentUserOwned: false, sort: "RANDOM", filterCreditsPromoted: true) {
            totalCount
          }
          summaryAudiobooks(limit: 3) {
            id
            coverUrl
            displayTitle
            displayAuthors
            url
          }
        }
      }
    }
  }
`;

const tags = gql`
  query fetchTags($featuredOnly: Boolean) {
    tags(featuredOnly: $featuredOnly) {
      id
      displayName
      url
    }
  }
`;

const topic = gql`
  query FetchTopic($topicName: String!) {
    topic(topicName: $topicName) {
      name
      url
    }
  }
`;

const userCollection = gql`
  query FetchUserCollection(
    $userCollectionId: ID!
    $page: Int
    $pageSize: Int
    $userAudiobooksSort: UserAudiobooksSort
  ) {
    userCollection(userCollectionId: $userCollectionId) {
      title
      totalDuration
      audiobooksCount

      userAudiobooks(page: $page, pageSize: $pageSize, sort: $userAudiobooksSort) {
        id
        progressStatus
        positionPercent
        playable

        audiobook {
          id
          url
          coverUrl
          displayTitle
          displayAuthors
          displayNarrators
          currentProduct {
            listingPrice
            discountPrice
            savingsPercent
            timeLeft
          }
        }

        userReview {
          id
          hasReview
          rating
        }
      }
    }
  }
`;

const userNotificationView = gql`
  query FetchCurrentUserNotificationView($notification: UserNotificationViewNotification!) {
    currentUserNotificationView(notification: $notification) {
      id
    }
  }
`;

const userReview = gql`
  query FetchUserReview($audiobookId: ID!) {
    currentUserReview(audiobookId: $audiobookId) {
      id
      rating
      review
    }
  }
`;

export const graphQLQueries = {
  audiobookPromotedCredits,
  currentAddress,
  currentGiftCardBalance,
  currentProfile,
  currentUserAudiobooks,
  currentUserCollections,
  currentUserCollectionsBasic,
  currentUserFollowedAuthors,
  currentUserSuggestedAuthors,
  promotionalBundle,
  promotionalBundles,
  recommendedWeakFollowAuthors,
  tags,
  tagSuggestedPeople,
  topic,
  userCollection,
  userReview,
  userNotificationView,
  ...audiobookQueries,
  ...blogQueries,
  ...collectionQueries,
  ...bookClubQueries,
  ...promotionQueries,
  ...shoppingCartQueries,
  ...wishlistQueries,
};
